import { createContext, useState, useEffect, useCallback, useContext, useRef, type ReactNode } from 'react';
import { apiClient } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { getDefaultFlags, type FeatureFlags } from './flag-definitions';

/** Interval in ms between polling heartbeats (used when EventSource is not available). */
const POLL_INTERVAL_MS = 30_000;

/** Seconds to wait before reconnecting the SSE connection after an error. */
const SSE_RECONNECT_DELAY_MS = 5_000;

export interface FeatureFlagContextType {
  flags: FeatureFlags;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<boolean>;
  /** 'sse' | 'polling' | 'fallback' — how flags are currently being synced */
  syncMode: 'sse' | 'polling' | 'fallback';
}

export const FeatureFlagContext = createContext<FeatureFlagContextType | null>(null);

interface FeatureFlagProviderProps {
  children: ReactNode;
}

export function FeatureFlagProvider({ children }: FeatureFlagProviderProps): JSX.Element {
  const { tenant, isLoading: authLoading } = useAuth();
  const tenantId = tenant?.id ?? '';
  const [flags, setFlags] = useState<FeatureFlags>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncMode, setSyncMode] = useState<'sse' | 'polling' | 'fallback'>('fallback');

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sseRef = useRef<EventSource | null>(null);

  // ── Fetch flags once (HTTP GET) ──────────────────────────────────────
  const fetchFlags = useCallback(async (): Promise<boolean> => {
    if (!tenantId) {
      setFlags(getDefaultFlags());
      setIsLoading(false);
      setSyncMode('fallback');
      return true;
    }

    try {
      const data = await apiClient.get<FeatureFlags>(`/tenants/${tenantId}/feature-flags`);
      /* Guarda de forma: `setFlags(data)` cru confiava na resposta. Se o
         endpoint devolvesse array, null ou outro envelope, TODA flag virava
         falsa em silencio e os modulos gated desapareciam do menu e das rotas
         sem erro nenhum — em ERP isso e chamado de suporte, nao bug visivel.
         Resposta fora de forma agora cai no default, igual a falha de rede. */
      if (!data || typeof data !== 'object' || Array.isArray(data)) {
        setError('Resposta de feature flags fora do formato esperado');
        setFlags(getDefaultFlags());
        return false;
      }
      setFlags(data);
      setError(null);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar feature flags';
      setError(message);
      setFlags(getDefaultFlags());
      return false;
    }
  }, [tenantId]);

  // ── Initial fetch ────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    /*
      Enquanto a sessao nao resolveu, `tenantId` e vazio e `fetchFlags` cai no
      default — onde `manufacturing` e false. As rotas protegidas por flag
      liam esse default como "desligado" e redirecionavam para /dashboard
      ANTES de a resposta do tenant chegar: abrir /manufacturing/boms direto
      na barra de endereco nunca abria a tela. Nao se decide flag de tenant
      com a sessao ainda carregando.
    */
    if (authLoading) {
      setIsLoading(true);
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      setIsLoading(true);
      const ok = await fetchFlags();
      if (cancelled) return;
      setIsLoading(false);
      if (ok && tenantId) {
        startSSE();
      } else if (tenantId) {
        startPolling();
      }
    })();

    return () => {
      cancelled = true;
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, authLoading]);

  // ── Helpers ──────────────────────────────────────────────────────────
  function cleanup(): void {
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }

  function startSSE(): void {
    cleanup();
    setSyncMode('sse');

    const url = `${window.location.origin}/api/v1/tenants/${tenantId}/feature-flags/stream`;
    const source = new EventSource(url, { withCredentials: true });
    sseRef.current = source;

    source.addEventListener('feature-flags', (event: MessageEvent) => {
      try {
        const data: FeatureFlags = JSON.parse(event.data);
        setFlags(data);
        setError(null);
      } catch {
        // ignore malformed events
      }
    });

    source.onerror = () => {
      source.close();
      sseRef.current = null;
      // Fall back to polling if SSE fails
      setSyncMode('polling');
      startPolling();

      // Attempt to reconnect SSE after a delay
      setTimeout(() => {
        if (tenantId) startSSE();
      }, SSE_RECONNECT_DELAY_MS);
    };
  }

  function startPolling(): void {
    if (pollingRef.current) return;

    setSyncMode('polling');
    pollingRef.current = setInterval(() => {
      fetchFlags().catch(() => {});
    }, POLL_INTERVAL_MS);
  }

  return (
    <FeatureFlagContext.Provider value={{ flags, isLoading, error, refresh: fetchFlags, syncMode }}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

// ── Re-export hooks for convenience ─────────────────────────────────────
export { useFeatureFlag, useAllFlags, useAnyFlag } from './feature-flags';

/**
 * Guard component that only renders children if a specific flag is enabled.
 * Optionally renders fallback content when the flag is disabled.
 */
interface FeatureFlagGuardProps {
  name: string;
  children: ReactNode;
  fallback?: ReactNode;
}

export function FeatureFlagGuard({ name, children, fallback = null }: FeatureFlagGuardProps): JSX.Element | null {
  const context = useContext(FeatureFlagContext);
  const isEnabled = context?.flags[name] ?? false;
  if (!isEnabled) return <>{fallback}</>;
  return <>{children}</>;
}
